/**
 * DeliveryRunView - Mobile-only delivery management view
 *
 * Shows packed orders for today, allows route optimisation,
 * reordering, deferring stops, adding custom stops, and
 * marking deliveries as complete.
 *
 * Deferred IDs and custom stops persist in localStorage (daily key rotation).
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { toLocalDateString } from '../../utils/dateUtils'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove
} from '@dnd-kit/sortable'
import {
  Navigation,
  Route,
  RotateCcw,
  CheckSquare,
  Loader2,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  MapPin,
  Plus,
  SkipForward
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import PageLayout from '../layout/PageLayout'
import { MobileOnlyGuard } from './MobileOnlyGuard'
import { DeliveryStopCard } from './DeliveryStopCard'
import { AddStopDialog } from './AddStopDialog'
import { fetchSalesWithDateRange, updateSaleStatus } from '../../supabase-client'
import { geocodeAddressCached } from '../../utils/geocoding'
import { optimiseRoute, generateGoogleMapsUrl, generateSingleDestinationUrl } from '../../utils/routing'
import { loadDeliveryState, saveDeliveryState } from '../../utils/deliveryStorage'

export default function DeliveryRunView({ user, onLogout }) {
  // State
  const [stops, setStops] = useState([])
  const [deliveredIds, setDeliveredIds] = useState(new Set())
  const [deferredIds, setDeferredIds] = useState(new Set())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [optimising, setOptimising] = useState(false)
  const [routeInfo, setRouteInfo] = useState(null)
  const [showDelivered, setShowDelivered] = useState(false)
  const [showDeferred, setShowDeferred] = useState(true)
  const [geocodingStatus, setGeocodingStatus] = useState('')
  const [addStopOpen, setAddStopOpen] = useState(false)

  // Track if initial load from localStorage is done
  const storageLoaded = useRef(false)

  // Drag sensors - configured for mobile touch
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8
      }
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  // Derived lists
  const activeStops = useMemo(
    () => stops.filter(s => !deliveredIds.has(s.id) && !deferredIds.has(s.id)),
    [stops, deliveredIds, deferredIds]
  )

  const deferredStopsList = useMemo(
    () => stops.filter(s => deferredIds.has(s.id) && !deliveredIds.has(s.id)),
    [stops, deferredIds, deliveredIds]
  )

  const deliveredStops = useMemo(
    () => stops.filter(s => deliveredIds.has(s.id)),
    [stops, deliveredIds]
  )

  // Load packed orders for today
  useEffect(() => {
    loadTodaysOrders()
  }, [])

  // Persist deferred/custom state changes to localStorage
  useEffect(() => {
    if (!storageLoaded.current) return

    const customStops = stops.filter(s => s.isCustomStop)
    saveDeliveryState({
      deferredIds: [...deferredIds],
      customStops
    })
  }, [deferredIds, stops])

  const loadTodaysOrders = async () => {
    setLoading(true)
    setError(null)

    try {
      const today = toLocalDateString(new Date())
      const sales = await fetchSalesWithDateRange(today, today)

      // Filter to packed orders only, delivery customers only
      const packedSales = sales
        .filter(s => s.status === 'packed')
        .filter(s => s.customer?.transmission === 'delivery')

      // Transform to delivery stops
      const deliveryStops = packedSales.map(sale => ({
        id: sale.id,
        saleId: sale.id,
        consignmentNo: sale.consignment_no,
        customerName: sale.customer?.name || 'Unknown Customer',
        branchName: sale.branch?.name || 'Main Company',
        address: sale.branch?.address || null,
        items: sale.sale_items?.map(item => ({
          id: item.id,
          productName: item.product_type?.name || 'Unknown Product',
          strain: item.product?.strain,
          quantity: item.quantity
        })) || [],
        lat: null,
        lng: null,
        isCustomStop: false
      }))

      // Load persisted state from localStorage
      const savedState = loadDeliveryState()
      storageLoaded.current = true

      // Restore deferred IDs (only those that still exist in today's sales)
      const validDeferredIds = savedState.deferredIds.filter(id =>
        deliveryStops.some(s => s.id === id)
      )
      setDeferredIds(new Set(validDeferredIds))

      // Merge custom stops back in
      const allStops = [...deliveryStops, ...savedState.customStops]
      setStops(allStops)

      if (allStops.length === 0) {
        setLoading(false)
        return
      }

      // Geocode addresses in background
      geocodeStops(allStops)
    } catch (err) {
      console.error('Failed to load orders:', err)
      setError('Failed to load today\'s orders')
    } finally {
      setLoading(false)
    }
  }

  const geocodeStops = async (stopsToGeocode) => {
    // Only geocode stops that don't already have coords (custom stops already have them)
    const needGeocoding = stopsToGeocode.filter(s => s.address && !s.lat)
    if (needGeocoding.length === 0) return

    setGeocodingStatus('Geocoding addresses...')

    const coordsMap = new Map()
    let geocoded = 0

    for (let i = 0; i < needGeocoding.length; i++) {
      const stop = needGeocoding[i]
      const coords = await geocodeAddressCached(stop.address)
      if (coords) {
        coordsMap.set(stop.id, coords)
        geocoded++
      }
      setGeocodingStatus(`Geocoded ${i + 1}/${needGeocoding.length} addresses`)
    }

    // Apply all coords at once
    setStops(prev => prev.map(s => {
      const coords = coordsMap.get(s.id)
      return coords ? { ...s, lat: coords.lat, lng: coords.lng } : s
    }))

    setGeocodingStatus(geocoded > 0 ? `${geocoded} addresses geocoded` : '')
    setTimeout(() => setGeocodingStatus(''), 3000)
  }

  // Optimise route using OSRM — only active stops
  const handleOptimiseRoute = async () => {
    const stopsWithCoords = activeStops.filter(s => s.lat && s.lng)

    if (stopsWithCoords.length < 2) {
      setError('Need at least 2 stops with valid addresses to optimise route')
      setTimeout(() => setError(null), 3000)
      return
    }

    setOptimising(true)
    setError(null)

    try {
      const waypoints = stopsWithCoords.map(s => ({ lat: s.lat, lng: s.lng }))
      const result = await optimiseRoute(waypoints)

      if (result.error) {
        setError(`Route optimisation failed: ${result.error}`)
        setTimeout(() => setError(null), 5000)
        return
      }

      // Reorder only active stops with coordinates
      const stopsWithoutCoords = activeStops.filter(s => !s.lat || !s.lng)
      const reorderedWithCoords = result.orderedIndices.map(i => stopsWithCoords[i])

      // Rebuild full stops array: reordered active, then ungeocoded active, then deferred, then delivered
      const deferredStops = stops.filter(s => deferredIds.has(s.id))
      const deliveredStopsList = stops.filter(s => deliveredIds.has(s.id))

      setStops([
        ...reorderedWithCoords,
        ...stopsWithoutCoords,
        ...deferredStops,
        ...deliveredStopsList
      ])

      setRouteInfo({
        distance: result.distance,
        duration: result.duration
      })
    } catch (err) {
      console.error('Route optimisation failed:', err)
      setError('Failed to optimise route')
      setTimeout(() => setError(null), 3000)
    } finally {
      setOptimising(false)
    }
  }

  // Reverse route order — only active stops
  const handleReverseRoute = () => {
    const reversed = [...activeStops].reverse()
    const deferredStops = stops.filter(s => deferredIds.has(s.id))
    const deliveredStopsList = stops.filter(s => deliveredIds.has(s.id))
    setStops([...reversed, ...deferredStops, ...deliveredStopsList])
  }

  // Mark stop as delivered
  const handleMarkDelivered = async (stopId) => {
    try {
      await updateSaleStatus(stopId, 'delivered')
      setDeliveredIds(prev => new Set([...prev, stopId]))
    } catch (err) {
      console.error('Failed to mark as delivered:', err)
      setError(err.message || 'Failed to update delivery status')
      setTimeout(() => setError(null), 3000)
    }
  }

  // Defer a stop
  const handleDefer = (stopId) => {
    setDeferredIds(prev => new Set([...prev, stopId]))
  }

  // Restore a deferred stop
  const handleUndefer = (stopId) => {
    setDeferredIds(prev => {
      const next = new Set(prev)
      next.delete(stopId)
      return next
    })
  }

  // Add a custom stop
  const handleAddCustomStop = (customStop) => {
    setStops(prev => [...prev, customStop])
  }

  // Remove a custom stop
  const handleRemoveCustomStop = (stopId) => {
    setStops(prev => prev.filter(s => s.id !== stopId))
  }

  // Navigate to all active stops
  const handleNavigateAll = () => {
    const stopsWithCoords = activeStops.filter(s => s.lat && s.lng)

    if (stopsWithCoords.length === 0) {
      setError('No stops with valid addresses to navigate')
      setTimeout(() => setError(null), 3000)
      return
    }

    const url = generateGoogleMapsUrl(stopsWithCoords)
    window.open(url, '_blank')
  }

  // Navigate to single stop
  const handleNavigateToStop = (stop) => {
    if (!stop.address && (!stop.lat || !stop.lng)) {
      setError('No address for this stop')
      setTimeout(() => setError(null), 3000)
      return
    }

    const url = generateSingleDestinationUrl(stop)
    window.open(url, '_blank')
  }

  // Handle drag end — only reorders within active stops
  const handleDragEnd = useCallback((event) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      setStops(items => {
        const oldIndex = items.findIndex(i => i.id === active.id)
        const newIndex = items.findIndex(i => i.id === over.id)
        return arrayMove(items, oldIndex, newIndex)
      })
    }
  }, [])

  return (
    <PageLayout
      title="Deliveries"
      user={user}
      onLogout={onLogout}
      showFooter={false}
    >
      <div className="h-full overflow-y-auto">
      <MobileOnlyGuard>
        <div className="pb-24">
          {/* Route Info Bar */}
          {routeInfo && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-center justify-between">
              <div className="flex items-center gap-4 text-sm">
                <span className="text-blue-700">
                  <strong>{routeInfo.distance}</strong> km
                </span>
                <span className="text-blue-700">
                  ~<strong>{routeInfo.duration}</strong> mins
                </span>
              </div>
              {geocodingStatus && (
                <span className="text-xs text-blue-600">{geocodingStatus}</span>
              )}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-center gap-2 text-red-700 text-sm">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 mb-4">
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={handleOptimiseRoute}
              disabled={optimising || activeStops.length < 2}
            >
              {optimising ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Route className="w-4 h-4 mr-2" />
              )}
              Optimise
            </Button>

            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={handleReverseRoute}
              disabled={activeStops.length < 2}
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Reverse
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={() => setAddStopOpen(true)}
            >
              <Plus className="w-4 h-4" />
            </Button>

            <Button
              variant="default"
              size="sm"
              className="flex-1 bg-blue-600 hover:bg-blue-700"
              onClick={handleNavigateAll}
              disabled={activeStops.length === 0}
            >
              <Navigation className="w-4 h-4 mr-2" />
              Navigate
            </Button>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400 mb-3" />
              <p className="text-gray-500">Loading today&apos;s orders...</p>
            </div>
          )}

          {/* Empty State */}
          {!loading && stops.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <MapPin className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">
                No deliveries today
              </h3>
              <p className="text-gray-500 text-sm">
                Pack some orders or add a custom stop
              </p>
            </div>
          )}

          {/* Active Stops List */}
          {!loading && activeStops.length > 0 && (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={activeStops.map(s => s.id)}
                strategy={verticalListSortingStrategy}
              >
                {activeStops.map((stop, index) => (
                  <DeliveryStopCard
                    key={stop.id}
                    stop={stop}
                    index={index}
                    isDelivered={false}
                    isDeferred={false}
                    isCustomStop={!!stop.isCustomStop}
                    onMarkDelivered={handleMarkDelivered}
                    onNavigate={handleNavigateToStop}
                    onDefer={handleDefer}
                    onRemoveCustomStop={handleRemoveCustomStop}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}

          {/* Deferred Section */}
          {deferredStopsList.length > 0 && (
            <div className="mt-6">
              <button
                className="flex items-center justify-between w-full py-2 text-sm text-gray-600"
                onClick={() => setShowDeferred(!showDeferred)}
              >
                <span className="flex items-center gap-2">
                  <SkipForward className="w-4 h-4 text-amber-600" />
                  Deferred ({deferredStopsList.length})
                </span>
                {showDeferred ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>

              {showDeferred && (
                <div className="mt-2">
                  {deferredStopsList.map((stop) => (
                    <DeliveryStopCard
                      key={stop.id}
                      stop={stop}
                      index={0}
                      isDelivered={false}
                      isDeferred={true}
                      isCustomStop={!!stop.isCustomStop}
                      onMarkDelivered={() => {}}
                      onNavigate={handleNavigateToStop}
                      onUndefer={handleUndefer}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Delivered Section */}
          {deliveredStops.length > 0 && (
            <div className="mt-6">
              <button
                className="flex items-center justify-between w-full py-2 text-sm text-gray-600"
                onClick={() => setShowDelivered(!showDelivered)}
              >
                <span className="flex items-center gap-2">
                  <CheckSquare className="w-4 h-4 text-green-600" />
                  Delivered ({deliveredStops.length})
                </span>
                {showDelivered ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>

              {showDelivered && (
                <div className="mt-2">
                  {deliveredStops.map((stop, index) => (
                    <DeliveryStopCard
                      key={stop.id}
                      stop={stop}
                      index={index}
                      isDelivered={true}
                      isDeferred={false}
                      isCustomStop={!!stop.isCustomStop}
                      onMarkDelivered={() => {}}
                      onNavigate={handleNavigateToStop}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </MobileOnlyGuard>
      </div>

      {/* Add Stop Dialog */}
      <AddStopDialog
        open={addStopOpen}
        onOpenChange={setAddStopOpen}
        onAddStop={handleAddCustomStop}
      />
    </PageLayout>
  )
}
